import styled from '@emotion/styled';
import React from 'react';
import { head, initial, last } from 'lodash';
import moment from 'moment';
import { ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { DiscountedDates, TourAvailability } from '@model/tours';
import { formatAmount, Price, YEAR_FORMAT } from '@model/common';
import { WarningBox } from '@styles/common';
import { Basket } from '@model/iceberg';
import { useSelector } from 'react-redux';
import { getBasket } from '@state/basket';
import { Header, TileDiscountBox } from '@components/common/banner/tile-discount/TileDiscountBox';
import { getTranslatedHTML } from '@util/common';

/* ***************** *
 *       Types       *
 * ***************** */
interface DiscountPriceProps {
  price: Price;
}

interface TourAvailabilityDiscountPriceDatesProps {
  discounts: Array<Array<TourAvailability>>;
  discountDepartureDates: DiscountedDates;
}

const PriceWrapper = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any)
}));

const OriginalPrice = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  margin: `0 ${theme.custom.spacing.xSmall}px 0 0`,
  textDecoration: 'line-through',
  fontWeight: 'normal'
}));

const DiscountedPrice = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any)
}));

const DiscountedPriceDetails = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  fontWeight: 'normal',
  fontSize: '1.4rem',
  margin: `${theme.custom.spacing.xSmall}px 0`
}));

const TourDiscountInner = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  columnGap: 40,
  alignItems: 'flex-start',
  flexWrap: 'nowrap',
  rowGap: theme.custom.spacing.large,
  ['div']: {
    flex: '1 0'
  }
}));

/* *********************
 *    DiscountPrice    *
 * *********************/
export const DiscountPrice = ({ price }: DiscountPriceProps) => {
  const t: any = useI18NextContext();
  const { discount, original, amount, currency } = price;

  return (
    <PriceWrapper>
      {original && <OriginalPrice>{formatAmount(original, currency)}</OriginalPrice>}
      {original && discount && <DiscountedPrice>{formatAmount(amount, currency)}</DiscountedPrice>}
      {discount && (
        <DiscountedPriceDetails>
          {t('tour-product__discount--price-details', {
            discount: formatAmount(discount, currency),
            code: discount / 100
          })}
        </DiscountedPriceDetails>
      )}
    </PriceWrapper>
  );
};

/* **************************
 *    DiscountPriceDates    *
 * **************************/
export const TourAvailabilityDiscountPriceDates = ({
  discounts,
  discountDepartureDates
}: TourAvailabilityDiscountPriceDatesProps) => {
  const t: any = useI18NextContext();

  if (!discountDepartureDates) return null;

  const discountedPackages = discounts.find((packages) => !!packages.length);

  if (!discountedPackages) return null;

  const {
    price: { currency }
  } = discountedPackages[0];

  const renderDiscounts = () =>
    Object.entries(discountDepartureDates).map(([amount, dates]) => {
      const numberAmount = Number(amount.replace('_', ''));
      const discount = formatAmount(numberAmount * 100, currency);
      const dateString = dates[0];
      const date = moment(dateString);
      const year = date.isValid()
        ? date.format(YEAR_FORMAT)
        : head(dateString.match(/\b\d{4}\b/)) || new Date().getFullYear();

      return (
        <div key={amount}>
          <Header>
            {t('tour-product__discount--label-title', {
              discount,
              year
            })}
          </Header>
          <p
            dangerouslySetInnerHTML={getTranslatedHTML(t, 'tour-product__discount--label-details-code', {
              code: numberAmount,
              currency: currency.symbol,
              discount,
              departureDates: `${
                dates.length === 1 ? dates[0] : `${initial(dates).join(', ')} ${t('common__label--and')} ${last(dates)}`
              }`
            })}
          />
        </div>
      );
    });

  return (
    <TileDiscountBox>
      <TourDiscountInner>{renderDiscounts()}</TourDiscountInner>
    </TileDiscountBox>
  );
};

/* *********************
 *    PromoCodeBanner  *
 * *********************/
export const PromoCodeBanner = () => {
  const t: any = useI18NextContext();
  const basket: Basket = useSelector(getBasket);
  const promoCode: string = basket.promoCode;

  return (
    <>
      {promoCode && (
        <WarningBox
          dangerouslySetInnerHTML={{
            __html: t('tour-product__discount--promo-code', { code: `<strong>${promoCode}</strong>` })
          }}
        />
      )}
    </>
  );
};
